import type {
	AnnualCroplandPayload,
	AnnualCroplandResponse,
	MinorSeason,
	MinorSeasonT2,
	ModuleFull,
	AnnualCroplandT2,
} from "@features/modules/annualCropland/types";
import { filterObjectProps, convertEmptyStringsToNull } from "@shared/modulePayload";
import { FEModules } from "../../../utils/moduleList";
import { FEModulesObj } from "../../../utils/moduleList";
import { apiSlice, publicApiSlice, TagTypes } from "../../apiSlice";
import { createModuleGetApi, createModuleUpdateApi } from "./factories/createModuleApi";

const includedPropList = [
	"id",
	"status",
	"ha_start",
	"ha_w",
	"ha_wo",
	"land_use_type_start",
	"land_use_type_w",
	"land_use_type_wo",
	"land_use_type_thread",
	"tillage_management_type_start",
	"tillage_management_type_w",
	"tillage_management_type_wo",
	"tillage_management_type_thread",
	"organic_input_type_start",
	"organic_input_type_w",
	"organic_input_type_wo",
	"organic_input_type_thread",
	"residue_management_type_start",
	"residue_management_type_w",
	"residue_management_type_wo",
	"residue_management_type_thread",
	"crop_yield_t2_start",
	"crop_yield_t2_w",
	"crop_yield_t2_wo",
	"crop_yield_t2_thread",
	"minorSeason",
	"activity",
	"land_use_change",
	"note",
	"area",
];
const minorSeasonPropsList = [
	"minor_land_use_type_start",
	"minor_land_use_type_w",
	"minor_land_use_type_wo",
	"minor_residue_management_type_start",
	"minor_residue_management_type_w",
	"minor_residue_management_type_wo",
	"minor_yield_start",
	"minor_yield_w",
	"minor_yield_wo",
];
const includedT2PropList = [
	"soc_t2_start",
	"soc_t2_w",
	"soc_t2_wo",
	"fmg_t2_start",
	"fmg_t2_w",
	"fmg_t2_wo",
	"fi_t2_start",
	"fi_t2_w",
	"fi_t2_wo",
	"biomass_t2_start",
	"biomass_t2_w",
	"biomass_t2_wo",
	"flu_t2_start",
	"flu_t2_w",
	"flu_t2_wo",
	"residue_availability_t2_start",
	"residue_availability_t2_w",
	"residue_availability_t2_wo",
	"minorSeason",
];

// Define constants for the module
const MODULE_TYPE = "AnnualCropland";
const TAG: TagTypes = "AnnualCropland";
const TAG_DEFAULTS: TagTypes = "AnnualCroplandDefaults";
const ENDPOINT = FEModulesObj[FEModules.AnnualCropland].path;
const t2PercentageProps = ["minor_biomass_factor_t2_start", "minor_biomass_factor_t2_w", "minor_biomass_factor_t2_wo"];

// const transformResponse = (response: AnnualCroplandResponse) => transformModuleResponse<AnnualCroplandResponse, ModuleFull>({
// 	response,
// 	includedPropList,
// 	includedT2PropList,
// 	t2PercentageProps
// })
const transformResponse = (response: AnnualCroplandResponse) => {
	if (response) {
		const moduleMinorSeasonData = filterObjectProps(
			response,
			minorSeasonPropsList,
		) as MinorSeason;

		const t2MinorSeasonData = filterObjectProps(response, t2PercentageProps) as MinorSeasonT2;

		const module = filterObjectProps(
			{ ...response, minorSeason: moduleMinorSeasonData },
			includedPropList,
		) as AnnualCroplandPayload;

		const t2 = filterObjectProps(
			{ ...response, minorSeason: t2MinorSeasonData },
			includedT2PropList,
		) as AnnualCroplandT2;

		return {
			module: module,
			tiertwo: t2,
		};
	}
	return null;
}

const annualCroplandApiSlice = createModuleGetApi<ModuleFull>({
	apiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	tagDefaults: TAG_DEFAULTS,
	transformResponse,
})

const publicAnnualCroplandApiSlice = createModuleGetApi<ModuleFull>({
	apiSlice: publicApiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	tagDefaults: TAG_DEFAULTS,
	transformResponse,
})

export const {
	useGetAnnualCroplandQuery,
	useGetAnnualCroplandDefaultsQuery
} = annualCroplandApiSlice;

export const {
	useGetAnnualCroplandQuery: usePublicGetAnnualCroplandQuery,
	useGetAnnualCroplandDefaultsQuery: usePublicGetAnnualCroplandDefaultsQuery
} = publicAnnualCroplandApiSlice;

const transformUpdatePayload = (data: Partial<ModuleFull>, activityId: number) => {
	const module = { ...data.module, ...data?.module?.minorSeason }; //there's max 1 minorSeason
	const t2 = { ...data.tiertwo, ...data?.tiertwo?.minorSeason }; //there's max 1 minorSeason
	const lucId = data?.module?.land_use_change;
	const payload = {
		...module,
		...t2,
	} as unknown as AnnualCroplandResponse;
	const { id, ...rest } = payload;
	const body = { ...rest, activity: activityId, land_use_change: lucId };
	convertEmptyStringsToNull(body);
	return { body, id };
}

const mutationApiSlice = createModuleUpdateApi<ModuleFull, AnnualCroplandResponse>({
	apiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	tagDefaults: TAG_DEFAULTS,
	transformUpdatePayload,
	includesLandUseChange: true,
})

export const {
	useUpdateAnnualCroplandMutation,
} = mutationApiSlice;

export const annualCroplandEndpoints = annualCroplandApiSlice.endpoints;
export const annualCroplandMutationEndpoints = mutationApiSlice.endpoints;
